import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { deleteAccount, reinstateAccount, logout } from '../../services/api';

function DeleteAccountSection() {
  const navigate = useNavigate();
  const {
    user,
    canChangePassword,
    deletionPending,
    deletionPermanentAt,
    deletionScheduledAt,
    tier,
    hasSubscription,
    stripeSubscriptionStatus,
    cancelAtPeriodEnd,
    clearUser,
    refreshProfile,
  } = useUser();

  // Delete mode state
  const [showModal, setShowModal] = useState(false);
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Reinstate mode state
  const [reinstating, setReinstating] = useState(false);
  const [reinstateError, setReinstateError] = useState('');

  // Check if user has an active paid subscription that blocks deletion
  const hasActivePaidSub =
    tier !== 'free' &&
    tier &&
    ['active', 'trialing', 'past_due'].includes(stripeSubscriptionStatus) &&
    !cancelAtPeriodEnd;

  const daysRemaining = deletionPermanentAt
    ? Math.max(0, Math.ceil((new Date(deletionPermanentAt) - new Date()) / (1000 * 60 * 60 * 24)))
    : 0;

  const handleDeleteSubmit = async () => {
    // Validate input
    if (canChangePassword && !password) {
      setDeleteError('Please enter your password.');
      return;
    }
    if (!canChangePassword && password !== 'DELETE') {
      setDeleteError('Please type DELETE to confirm.');
      return;
    }

    setDeleting(true);
    setDeleteError('');

    const result = await deleteAccount(password, reason);

    if (result.error) {
      setDeleteError(result.error);
      setDeleting(false);
      return;
    }

    // Success - log user out
    logout();
    clearUser();
    navigate('/login');
  };

  const handleReinstate = async () => {
    setReinstating(true);
    setReinstateError('');

    const result = await reinstateAccount();

    if (result.error) {
      setReinstateError(result.error);
      setReinstating(false);
      return;
    }

    // Success - refresh profile to clear deletion state
    await refreshProfile();
    setReinstating(false);
  };

  // --- Mode B: Deletion Pending - Show Reinstatement UI ---
  if (deletionPending) {
    return (
      <section style={{
        backgroundColor: '#1a1a1a',
        borderRadius: '8px',
        border: '1px solid #10b981',
        padding: '24px',
      }}>
        <h2 style={{ color: '#10b981', fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>
          Reinstate Your Account
        </h2>

        <div style={{
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          border: '1px solid rgba(16, 185, 129, 0.2)',
          borderRadius: '6px',
          padding: '16px',
          marginBottom: '16px',
        }}>
          <p style={{ color: '#d1d5db', fontSize: '14px', margin: '0 0 8px 0', lineHeight: '1.5' }}>
            Your account is scheduled for permanent deletion in{' '}
            <strong style={{ color: '#fff' }}>{daysRemaining} days</strong>.
          </p>
          {deletionScheduledAt && (
            <p style={{ color: '#9ca3af', fontSize: '13px', margin: '0 0 4px 0' }}>
              Requested on: {new Date(deletionScheduledAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          )}
          {deletionPermanentAt && (
            <p style={{ color: '#9ca3af', fontSize: '13px', margin: 0 }}>
              Permanent deletion on: {new Date(deletionPermanentAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          )}
        </div>

        <p style={{ color: '#d1d5db', fontSize: '14px', marginBottom: '16px', lineHeight: '1.5' }}>
          You can cancel the deletion and keep your account by clicking the button below.
          All your data, saved contracts, and watchlist groups will be preserved.
        </p>

        {reinstateError && (
          <div style={{
            padding: '10px 14px',
            marginBottom: '12px',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '6px',
            color: '#fca5a5',
            fontSize: '13px',
          }}>
            {reinstateError}
          </div>
        )}

        <button
          onClick={handleReinstate}
          disabled={reinstating}
          style={{
            padding: '10px 24px',
            backgroundColor: reinstating ? '#374151' : '#10b981',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: reinstating ? 'not-allowed' : 'pointer',
            opacity: reinstating ? 0.6 : 1,
            transition: 'all 0.2s',
          }}
        >
          {reinstating ? 'Reinstating...' : 'Cancel Deletion & Reinstate Account'}
        </button>
      </section>
    );
  }

  // --- Mode A: No Pending Deletion - Show Delete UI ---
  return (
    <>
      <section style={{
        backgroundColor: '#1a1a1a',
        borderRadius: '8px',
        border: '1px solid rgba(239, 68, 68, 0.3)',
        padding: '24px',
      }}>
        <h2 style={{ color: '#ef4444', fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>
          Danger Zone
        </h2>

        <p style={{ color: '#d1d5db', fontSize: '14px', marginBottom: '16px', lineHeight: '1.5' }}>
          Once you delete your account, your data will be retained for 90 days and then permanently removed.
          During this period, you can still log in and reinstate your account.
        </p>

        {/* Active subscription warning */}
        {hasActivePaidSub && (
          <div style={{
            padding: '12px 16px',
            marginBottom: '16px',
            backgroundColor: 'rgba(234, 179, 8, 0.1)',
            border: '1px solid rgba(234, 179, 8, 0.3)',
            borderRadius: '6px',
          }}>
            <p style={{ color: '#fbbf24', fontSize: '13px', margin: '0 0 8px 0', fontWeight: '500' }}>
              You have an active paid subscription.
            </p>
            <p style={{ color: '#d1d5db', fontSize: '13px', margin: '0 0 8px 0' }}>
              Please cancel your subscription before deleting your account.
            </p>
            <button
              onClick={() => navigate('/subscription')}
              style={{
                padding: '6px 16px',
                backgroundColor: 'transparent',
                color: '#218aff',
                border: '1px solid #218aff',
                borderRadius: '4px',
                fontSize: '13px',
                cursor: 'pointer',
              }}
            >
              Manage Subscription
            </button>
          </div>
        )}

        <button
          onClick={() => setShowModal(true)}
          disabled={hasActivePaidSub}
          style={{
            padding: '10px 24px',
            backgroundColor: hasActivePaidSub ? '#374151' : 'transparent',
            color: hasActivePaidSub ? '#6b7280' : '#ef4444',
            border: `1px solid ${hasActivePaidSub ? '#374151' : '#ef4444'}`,
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: hasActivePaidSub ? 'not-allowed' : 'pointer',
            opacity: hasActivePaidSub ? 0.6 : 1,
            transition: 'all 0.2s',
          }}
        >
          Delete Account
        </button>
      </section>

      {/* Delete Confirmation Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
        }}>
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #333',
            borderRadius: '12px',
            padding: '28px',
            maxWidth: '440px',
            width: '90%',
          }}>
            <h3 style={{ color: '#ef4444', fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>
              Confirm Account Deletion
            </h3>

            <p style={{ color: '#d1d5db', fontSize: '14px', marginBottom: '20px', lineHeight: '1.5' }}>
              This action will schedule your account for permanent deletion in 90 days.
              You will be logged out immediately.
            </p>

            {/* Password or DELETE confirmation */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', color: '#9ca3af', fontSize: '13px', marginBottom: '6px' }}>
                {canChangePassword
                  ? 'Enter your password to confirm'
                  : 'Type DELETE to confirm'}
              </label>
              <input
                type={canChangePassword ? 'password' : 'text'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={canChangePassword ? 'Your password' : 'Type DELETE'}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  backgroundColor: '#111',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: '#fff',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Optional reason */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', color: '#9ca3af', fontSize: '13px', marginBottom: '6px' }}>
                Reason for leaving (optional)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Help us improve by sharing your reason..."
                maxLength={255}
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  backgroundColor: '#111',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: '#fff',
                  fontSize: '14px',
                  outline: 'none',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
              />
              <p style={{ color: '#6b7280', fontSize: '11px', marginTop: '4px' }}>
                {reason.length}/255
              </p>
            </div>

            {deleteError && (
              <div style={{
                padding: '10px 14px',
                marginBottom: '12px',
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '6px',
                color: '#fca5a5',
                fontSize: '13px',
              }}>
                {deleteError}
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowModal(false);
                  setPassword('');
                  setReason('');
                  setDeleteError('');
                }}
                disabled={deleting}
                style={{
                  padding: '10px 20px',
                  backgroundColor: 'transparent',
                  color: '#d1d5db',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteSubmit}
                disabled={deleting}
                style={{
                  padding: '10px 20px',
                  backgroundColor: deleting ? '#374151' : '#ef4444',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  opacity: deleting ? 0.6 : 1,
                }}
              >
                {deleting ? 'Deleting...' : 'Delete My Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default DeleteAccountSection;
